import {
  ERROR_MOVE_CANNOT_DROP,
  ERROR_MOVE_INCOMING_PARENT,
  ERROR_MOVE_TO_DESCENDANT,
  NodeApi,
} from '@candulabs/craft';

const errorMatches = (message: string, error: string) => message.endsWith(error);

const errorText = (dropTarget: string, draggedNode: string, error: Error) => {
  const { message } = error;

  switch (true) {
    case errorMatches(message, ERROR_MOVE_INCOMING_PARENT):
    case errorMatches(message, ERROR_MOVE_CANNOT_DROP):
      return `You can't place ${draggedNode} inside ${dropTarget}`;
    case errorMatches(message, ERROR_MOVE_TO_DESCENDANT):
      return `You can't place ${draggedNode} inside itself`;
    default:
      return message;
  }
};

export const createErrorMessage = (
  node: NodeApi | undefined,
  draggedNodeIds: string[],
  getNode: (nodeId: string) => NodeApi,
): true | string => {
  if (!node) {
    return '';
  }
  try {
    node.isDroppable(draggedNodeIds, (err: string) => {
      throw err;
    });
    return true;
  } catch (error) {
    const draggedNode =
      draggedNodeIds.length > 1 ? 'these nodes' : getNode(draggedNodeIds[0]).displayName();
    return errorText(node.displayName(), draggedNode, error);
  }
};

export const nodeLayerScale = (nthLayerDisplayed: number) => {
  if (nthLayerDisplayed > 14) {
    return 'xsmall';
  }
  if (nthLayerDisplayed > 7) {
    return 'small';
  }
  return 'normal';
};

export const getMaxNth = (
  nodeId: string,
  expandedNodes: Record<string, boolean>,
  getNode: (nodeId: string) => NodeApi,
  length = 0,
): number => {
  const node = getNode(nodeId);
  if (expandedNodes[nodeId] && node.isCanvas()) {
    return node.childNodes().reduce((maxLength, childNodeId) => {
      const childNodeDepth = getMaxNth(childNodeId, expandedNodes, getNode, length + 1);

      return Math.max(maxLength, childNodeDepth);
    }, length);
  }
  return length;
};

export const isLastNodeOfParent = (nodeId: string, getNode: (nodeId: string) => NodeApi) => {
  const node = getNode(nodeId);
  return (
    !node.isRoot() &&
    node.getParent().childNodes().indexOf(nodeId) === node.getParent().childNodes().length - 1
  );
};

export const createLastChildNodes = (
  nodeId: string,
  getNode: (nodeId: string) => NodeApi,
  hoveredZones: Record<string, { showDropIn?: boolean; showDropAfter?: boolean }>,
): Record<string, { showDropIn?: boolean; showDropAfter?: boolean }> => {
  const childNodes = getNode(nodeId).childNodes();
  if (childNodes.length) {
    const lastChildNodeId = childNodes[childNodes.length - 1];
    return createLastChildNodes(lastChildNodeId, getNode, {
      ...hoveredZones,
      [lastChildNodeId]: { showDropAfter: true },
    });
  }
  return { ...hoveredZones, [nodeId]: { ...hoveredZones[nodeId], showDropIn: true } };
};

export const createDisplayableAreas = (
  nodeId: string,
  getNode: (nodeId: string) => NodeApi,
  hoveredZones: Record<string, { showDropIn?: boolean; showDropAfter?: boolean }>,
): Record<string, { showDropIn?: boolean; showDropAfter?: boolean }> => {
  if (isLastNodeOfParent(nodeId, getNode)) {
    const parentNodeId = getNode(nodeId).getParent().nodeId;
    return createDisplayableAreas(parentNodeId, getNode, {
      ...hoveredZones,
      [parentNodeId]: { showDropAfter: true },
    });
  }
  return hoveredZones;
};
